/**
 * CampaignConditionsList
 *
 * The "CONDITIONS" block of a campaign card: a centered header followed by one
 * centered row per condition description, each followed by a thin full-width
 * separator. Mirrors Flutter `CampaignListItem`
 * (lib/presentation/screens/campaigns/widgets/campaign_list_item.dart:80-124):
 * the caller renders this block whenever `conditions != null` (matching
 * Flutter's `Visibility(visible: campaign.conditions != null)` at :81), so an
 * explicit empty array shows the header with zero rows.
 */
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { Divider } from '@/presentation/components/common/Divider';
import type { CampaignConditionEntity } from '@/domain/entities/campaign/campaignConditionEntity';

interface CampaignConditionsListProps {
  conditions: CampaignConditionEntity[];
}

export const CampaignConditionsList = ({ conditions }: CampaignConditionsListProps) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div
        style={{
          marginBottom: responsive.spacing.sm,
          color: theme.text.secondary,
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.semibold,
          textAlign: 'center',
        }}
      >
        {l10n.campaigns.conditions}
      </div>
      {conditions.map((condition, index) => (
        <div
          key={`${condition.description}-${index}`}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
        >
          <div
            style={{
              color: theme.text.secondary,
              fontSize: responsive.font.base,
              textAlign: 'center',
            }}
          >
            {condition.description}
          </div>
          <Divider margin={responsive.spacing.lg} />
        </div>
      ))}
    </div>
  );
};
